<template>
  <div>
    <section id="work">
      <v-container>
        <p
          data-aos="fade-up"
          data-aos-duration="1500"
          :class="
            $vuetify.breakpoint.xs
              ? 'section-title-responsive text-center'
              : 'section-title'
          "
        >
          {{ $t("work").toUpperCase() }}
        </p>

        <div class="grid-container">
          <v-hover
            class="d-inline-block"
            v-for="card in sortedCards"
            :key="card.id"
            v-slot="{ hover }"
          >
            <v-card
              data-aos="fade-up"
              class="d-flex elevation-0 card-full-height"
              @click="openWork(card)"
            >
              <div
                class="img-container"
                :style="{
                  backgroundImage: `url(${card.img})`,
                }"
              >
                <span v-if="card.highlight" class="work-badge">
                  <span class="work-badge-dot"></span>
                  {{ $t(card.highlight) }}
                </span>

                <div
                  :class="[
                    'container-info',
                    {
                      'opacity-info': !hover && !$vuetify.breakpoint.smAndDown,
                    },
                  ]"
                >
                  <div class="d-flex mb-1">
                    <p
                      class="card-year font-weight-black grey--text text--lighten-3 mb-0 mx-2"
                    >
                      {{ card.year }}
                    </p>
                    <p class="card-title font-weight-black white--text mb-0">
                      {{ card.shortTitle }}
                    </p>
                  </div>

                  <div class="d-flex">
                    <v-chip
                      small
                      v-for="chip in card.tech"
                      :key="chip.id"
                      :text-color="chip.color"
                      class="ml-2"
                      :color="chip.background"
                      >{{ chip.name }}</v-chip
                    >
                  </div>
                </div>
              </div>
            </v-card>
          </v-hover>
        </div>
      </v-container>
    </section>

    <v-bottom-sheet
      v-model="dialogProject"
      :fullscreen="$vuetify.breakpoint.xs"
      scrollable
    >
      <v-card class="rounded-xxl-b-0">
        <v-card-title class="d-flex justify-end">
          <v-btn icon dark @click="closeWork()">
            <v-icon color="#242424">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="d-flex flex-column justify-center">
            <span
              class="text-h4 text-center font-weight-bold grey--text text--darken-4"
              >{{ workSelected?.title.toUpperCase() }}</span
            >

            <span
              v-if="workSelected?.subtitle"
              class="font-weight-medium grey--text text--darken-3 text-center mb-2"
            >
              - {{ workSelected?.subtitle }} -
            </span>

            <div
              v-if="workSelected?.highlight"
              class="d-flex justify-center mb-3"
            >
              <span class="work-badge work-badge--inline">
                <span class="work-badge-dot"></span>
                {{ $t(workSelected.highlight) }}
              </span>
            </div>

            <div class="d-flex justify-center mb-4">
              <v-chip
                small
                v-for="chip in workSelected?.tech"
                :key="chip.id"
                :text-color="chip.color"
                class="ml-2"
                :color="chip.background"
                >{{ chip.name }}</v-chip
              >
            </div>
          </div>

          <div class="d-flex flex-column justify-center align-center">
            <img
              v-if="workSelected != null"
              class="mb-5 rounded-xxl dialog-img"
              :src="workSelected.images[1]"
              :alt="workSelected.title"
              style="max-width: 800px"
            />

            <p
              v-html="
                workSelected != null
                  ? $i18n.locale == 'en'
                    ? workSelected.desc
                    : workSelected.desc_esp
                  : ''
              "
              class="text-justify px-3 project-desc"
              style="max-width: 800px"
            ></p>
          </div>

          <v-tabs v-model="tab" color="green darken-4" centered fixed-tabs>
            <v-tabs-slider color="green darken-4"></v-tabs-slider>

            <v-tab>
              {{ workSelected?.mobileOnly ? $t("mobile") : "Web" }}
            </v-tab>
            <v-tab v-if="workSelected?.images.length > 2">
              {{ $t("mobile") }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item>
              <div class="d-flex justify-center">
                <img
                  v-if="workSelected != null"
                  class="mt-5 rounded-lg dialog-img"
                  :src="workSelected.images[0]"
                  :alt="`${workSelected.title} - ${
                    workSelected.mobileOnly ? $t('mobile') : 'Web'
                  }`"
                  style="max-height: 800px; object-fit: contain"
                />
              </div>
            </v-tab-item>

            <v-tab-item v-if="workSelected?.images.length > 2">
              <div class="d-flex justify-center">
                <img
                  class="mt-5 rounded-lg dialog-img"
                  :src="workSelected.images[2]"
                  :alt="`${workSelected.title} - ${$t('mobile')}`"
                  style="width: auto"
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
import workPsico from "@/assets/images/workPsico.webp";
import imgPsicoEdgarTejo from "@/assets/images/edgarTrejoImg_1.webp";
import imgPsicoEdgarTrejoFullMobile from "@/assets/images/edgar-mobile.webp";
import imgPsicoEdgarTrejoFullWeb from "@/assets/images/edgar-web.webp";

import workNutrio from "../assets/images/nutriologaWork.webp";
import imgNutriologa from "../assets/images/work/nutriologa/imgNutriologa.webp";
import imgNutriologaFullWeb from "../assets/images/work/nutriologa/Nutriologa.webp";

import workTodo from "../assets/images/todoWork.webp";
import imgTodo from "@/assets/images/work/todo/imgTodo.webp";
import imgTodoFullWeb from "../assets/images/work/todo/Todo.webp";
import imgTodoFullMobile from "@/assets/images/work/todo/Todo-mobile.webp";

import workCyberpuerta from "../assets/images/work/cyberpuerta/cyber.webp";
import imgCyberpuertaFullWeb from "../assets/images/work/cyberpuerta/cyberpuertaFullWeb.webp";

import workWeather from "../assets/images/work/weatherApp/imgWeather.webp";
import imgWeather from "../assets/images/work/weatherApp/WeatherApp.webp";
import imgWeatherFullWeb from "../assets/images/work/weatherApp/weatherFullWeb.webp";

import workBlockDay from "../assets/images/work/blockDay/thumbnail.webp";
import imgBlockDay from "../assets/images/work/blockDay/hero.webp";
import imgBlockDayFullApp from "../assets/images/work/blockDay/fullApp.png";

import workTambos from "../assets/images/work/reconstructuraTambos/thumbnail.webp";
import imgTambos from "../assets/images/work/reconstructuraTambos/hero.webp";
import imgTambosFullWeb from "../assets/images/work/reconstructuraTambos/fullweb.webp";

export default {
  name: "WorkSection",

  computed: {
    // Del mas nuevo al mas viejo; a mismo año, el ultimo agregado va primero
    sortedCards() {
      return [...this.cards].sort((a, b) => b.year - a.year || b.id - a.id);
    },
  },

  data: () => ({
    cards: [
      {
        id: 1,
        title: "Edgar Trejo",
        subtitle: "Landing Page",
        shortTitle: "Landing Page",
        year: 2024,
        img: workPsico,
        desc: `I designed and developed a fully responsive landing page for a psychologist specializing in mental health and emotional well-being services. The goal was to create a welcoming and professional platform that could effectively communicate the psychologist's expertise while encouraging potential clients to get in touch. To achieve this, I implemented a user-centered design approach, focusing on clean aesthetics and an intuitive layout that inspires trust and comfort. The landing page prominently features key elements such as service descriptions, a contact form, and strategically placed calls-to-action to maximize user engagement and drive conversions.
<br> <br> The design was fully optimized for different devices, ensuring a seamless experience whether accessed on mobile, tablet, or desktop. Leveraging technologies like HTML, CSS, JavaScript, Vue.js, and Vuetify, I built a scalable and efficient structure that aligns with modern web development standards. This project allowed me to demonstrate my ability to understand a client's vision, translate it into a functional digital product, and create a meaningful online presence for specialized professional services.`,
        desc_esp: `Diseñé y desarrollé una landing page totalmente responsiva para un psicólogo especializado en servicios de salud mental y bienestar emocional.  El objetivo era crear una plataforma acogedora y profesional que pudiera comunicar eficazmente la experiencia del psicólogo y, al mismo tiempo, animar a los clientes potenciales a ponerse en contacto. Para lograrlo, apliqué un enfoque de diseño centrado en el usuario, centrándome en una estética limpia y un diseño intuitivo que inspira confianza y comodidad. La landing page cuenta con elementos clave como descripciones de servicios, un formulario de contacto y llamadas a la acción estratégicamente situadas para maximizar el compromiso del usuario e impulsar las conversiones.
        <br> <br> El diseño se optimizó completamente para diferentes dispositivos, garantizando una experiencia fluida tanto si se accede desde un móvil, una tableta o una PC. Aprovechando tecnologías como HTML, CSS, JavaScript, Vue.js y Vuetify, construí una estructura escalable y eficiente que se alinea con los estándares modernos de desarrollo web. Este proyecto me permitió demostrar mi capacidad para entender la visión de un cliente, traducirla en un producto digital funcional y crear una presencia en línea significativa para servicios profesionales especializados.`,
        images: [
          imgPsicoEdgarTrejoFullWeb,
          imgPsicoEdgarTejo,
          imgPsicoEdgarTrejoFullMobile,
        ],
        tech: [
          {
            name: "Vue.js",
            color: "grey--text text--darken-4",
            background: "#42B883",
            id: 1,
          },
          {
            name: "JavaScript",
            color: "grey--text text--darken-4",
            background: "#F0D900",
            id: 2,
          },
          {
            name: "HTML",
            color: "white",
            background: "#F16529",
            id: 3,
          },
          {
            name: "CSS",
            color: "white",
            background: "#13A1DF",
            id: 4,
          },
        ],
      },
      {
        id: 2,
        title: "Nutriologa",
        subtitle: "Landing Page",
        shortTitle: "Landing Page",
        year: 2025,
        img: workNutrio,
        desc: `Modern and user-friendly landing page design for a professional nutritionist. This layout focuses on clarity and accessibility, highlighting key services, credentials, and client benefits. The design features a clean and inviting aesthetic with a well-structured layout, ensuring an intuitive user experience. Emphasizing trust and professionalism, it includes a dedicated ‘About Me’ section, service descriptions, and a contact form to enhance client engagement.`,
        desc_esp: `Landing page diseñada para una nutrióloga profesional, enfocada en destacar sus servicios, experiencia y la confianza que brinda a sus pacientes. El diseño presenta una estética limpia y moderna, con una paleta de colores frescos y naturales que evocan bienestar y salud. Sección por sección, la página guía al usuario a través de los servicios ofrecidos, el perfil profesional de la nutrióloga, testimonios de pacientes satisfechos y datos de contacto. Gracias a un diseño responsivo y una estructura intuitiva, la navegación es fluida y accesible en cualquier dispositivo.`,
        images: [imgNutriologaFullWeb, imgNutriologa],
        tech: [
          {
            name: "Figma",
            color: "white",
            background: "#9C27B0",
            id: 1,
          },
        ],
      },
      {
        id: 3,
        title: "To Do App",
        subtitle: "Task Management Application",
        shortTitle: "To Do App",
        year: 2025,
        img: workTodo,
        desc: `A task management application designed to help users organize and prioritize their daily activities efficiently. The app features an intuitive and easy-to-use interface, allowing users to create, mark as completed, and delete tasks with ease. It includes functionalities such as filtering by completed or pending tasks and a light/dark mode, providing a personalized experience for each user.
              <br> <br> The responsive design ensures the app works seamlessly on both mobile and desktop devices, offering a smooth experience across all platforms. What I enjoyed most about this project was building it entirely with HTML, CSS, and vanilla JavaScript, without any frameworks or external libraries, reinforcing my JavaScript fundamentals, DOM manipulation skills, and best practices in web design.
              <br> <br> <a class="project-cta" href="https://todo-delta-brown.vercel.app" target="_blank">Visit my Todo App!</a>`,
        desc_esp: `Aplicación de gestión de tareas diseñada para ayudar a los usuarios a organizar y priorizar sus actividades diarias de manera eficiente. La aplicación presenta una interfaz intuitiva y fácil de usar, que permite crear, marcar como completadas y eliminar tareas con facilidad. Incluye funcionalidades como filtrado por tareas completas o pendientes y un modo light/dark, asegurando una experiencia personalizada para cada usuario.
                  <br> <br> El diseño responsivo garantiza que la aplicación funcione perfectamente en dispositivos móviles y de escritorio, proporcionando una experiencia fluida en cualquier plataforma. Lo que más disfruté de este proyecto fue programar completamente en HTML, CSS y JavaScript puro, sin frameworks ni librerías externas, reforzando mis fundamentos de JavaScript, manipulación del DOM y buenas prácticas de diseño web.
                  <br> <br> <a class="project-cta" href="https://todo-delta-brown.vercel.app" target="_blank">Visita mi Todo App!</a>`,
        images: [imgTodoFullWeb, imgTodo, imgTodoFullMobile],
        tech: [
          {
            name: "JavaScript",
            color: "grey--text text--darken-4",
            background: "#F0D900",
            id: 1,
          },
          {
            name: "HTML",
            color: "white",
            background: "#F16529",
            id: 2,
          },
          {
            name: "CSS",
            color: "white",
            background: "#13A1DF",
            id: 3,
          },
        ],
      },
      {
        id: 4,
        title: "Cyberpuerta Redesign",
        subtitle: "Redesign UI Design",
        shortTitle: "Cyberpuerta Redesign",
        year: 2025,
        img: workCyberpuerta,
        desc: `This project is a conceptual redesign of the Cyberpuerta website, created in Figma with the goal of enhancing both user experience and the visual aesthetics of the platform. In this redesign, each product card was given a unique layout to highlight the most relevant information, while increased spacing provides a sense of “breathing room” and improves readability. The brand’s primary color was used strategically for key actions and interactions, guiding the user’s attention to important elements. The overall design is more minimalist, simplifying the interface and enhancing clarity and usability. This project allowed me to explore how small design decisions and visual hierarchy can significantly improve user experience and strengthen brand identity.`,
        desc_esp: `Este proyecto es un rediseño conceptual de la página web de Cyberpuerta, realizado en Figma, con el objetivo de mejorar la experiencia de usuario y la estética visual de la plataforma. En este rediseño, a cada card de producto se le aplicó un diseño único para resaltar la información más relevante, mientras que los espaciados más amplios generan una sensación de “respiro” y facilitan la lectura. El color principal de la marca se utilizó estratégicamente en acciones e interacciones importantes, guiando la atención del usuario hacia los elementos clave, y el enfoque general del diseño es más minimalista, simplificando la interfaz y potenciando la claridad y la usabilidad. Este proyecto me permitió explorar cómo pequeñas decisiones de diseño y la jerarquía visual pueden mejorar la experiencia del usuario y reforzar la identidad de la marca.`,
        images: [imgCyberpuertaFullWeb, workCyberpuerta],
        tech: [
          {
            name: "Figma",
            color: "white",
            background: "#9C27B0",
            id: 1,
          },
        ],
      },
      {
        id: 5,
        title: "Weather App",
        subtitle: "Weather Application",
        shortTitle: "Weather App",
        year: 2025,
        img: workWeather,
        desc: `A weather web application built to display current conditions, hourly forecasts, and a 7-day outlook in a clear and accessible way. Users can search any location using a geocoding search (Nominatim API) and retrieve precise coordinates; weather data is fetched from the Open-Meteo API to provide the current weather, hourly details, and a summary for the next seven days. The interface emphasizes readability: prioritized information (current temperature, feels-like, short description), an hourly view for upcoming hours, and a 7-day view showing highs, lows, and conditions.
        <br> <br> Technically, the project demonstrates integration with public APIs, asynchronous request handling and error management, and a responsive design that works smoothly on mobile and desktop. This challenge allowed me to deepen my skills in REST API integration with JavaScript, improve search and loading/error state handling, and craft a minimal, user-friendly UI that makes weather data easy to understand at a glance.
        <br> <br> <a class="project-cta" href="https://weather-app-wine-rho.vercel.app/" target="_blank">Visit the project!</a>`,
        desc_esp: `Aplicación web de clima desarrollada para mostrar el clima actual, el pronóstico por horas y el pronóstico a 7 días de forma clara y accesible. El usuario puede buscar cualquier ubicación mediante un buscador (geocodificación con la API de Nominatim) y obtener con precisión la localización solicitada; los datos meteorológicos se consultan con la API de Open-Meteo para mostrar el estado actual, el detalle por hora y el resumen de los próximos siete días. La interfaz prioriza la legibilidad: información jerarquizada (temperatura actual, sensación, descripción breve), un carrusel o tabla horaria para consultar las horas siguientes y una vista de 7 días con máximas, mínimas y condiciones.
        <br> <br> Desde el punto de vista técnico, el proyecto integra llamadas a APIs públicas, manejo de respuestas asíncronas y errores, y presenta un diseño responsivo que funciona correctamente en móviles y escritorio. El reto me permitió profundizar en la integración de APIs REST desde JavaScript, optimizar la experiencia de búsqueda y manejo de estados de carga/errores, y diseñar una UI usable y minimalista que facilita la interpretación rápida del pronóstico.
        <br> <br> <a class="project-cta" href="https://weather-app-wine-rho.vercel.app/" target="_blank">Visita el proyecto!</a>`,
        images: [imgWeatherFullWeb, imgWeather],
        tech: [
          {
            name: "JavaScript",
            color: "grey--text text--darken-4",
            background: "#F0D900",
            id: 1,
          },
          {
            name: "HTML",
            color: "white",
            background: "#F16529",
            id: 2,
          },
          {
            name: "CSS",
            color: "white",
            background: "#13A1DF",
            id: 3,
          },
        ],
      },
      {
        id: 6,
        title: "Reconstructora de Tambos",
        subtitle: "Landing Page",
        shortTitle: "Reconstructora de Tambos",
        year: 2026,
        img: workTambos,
        desc: `A fully responsive landing page developed for Reconstructora de Tambos, a company specialized in the reconstruction and repair of industrial drums (tambos). The goal was to build a professional and trustworthy online presence that clearly communicates the company's services and makes it easy for potential clients to get in touch. I focused on a clean, straightforward layout with a clear visual hierarchy, highlighting the services offered, the company's value, and prominent calls-to-action to drive customer inquiries.
        <br> <br> The site was built from scratch with Vue.js, HTML, CSS, and JavaScript, and optimized to deliver a smooth experience across mobile, tablet, and desktop devices. This project allowed me to turn a client's needs into a functional, scalable digital product that strengthens their brand image and supports their business growth.
        <br> <br> <a class="project-cta" href="https://reconstructora-tambos-hqxmx73ea-melvin-durans-projects.vercel.app" target="_blank">Visit the project!</a>`,
        desc_esp: `Landing page totalmente responsiva desarrollada para Reconstructora de Tambos, una empresa especializada en la reconstrucción y reparación de tambos industriales. El objetivo fue crear una presencia en línea profesional y confiable que comunicara claramente los servicios de la empresa y facilitara a los clientes potenciales ponerse en contacto. Me enfoqué en un diseño limpio y directo, con una jerarquía visual clara que destaca los servicios ofrecidos, el valor de la empresa y llamadas a la acción visibles para impulsar las solicitudes de los clientes.
        <br> <br> El sitio se construyó desde cero con Vue.js, HTML, CSS y JavaScript, y se optimizó para ofrecer una experiencia fluida en dispositivos móviles, tabletas y de escritorio. Este proyecto me permitió convertir las necesidades de un cliente en un producto digital funcional y escalable que refuerza la imagen de su marca y apoya el crecimiento de su negocio.
        <br> <br> <a class="project-cta" href="https://reconstructora-tambos-hqxmx73ea-melvin-durans-projects.vercel.app" target="_blank">Visita el proyecto!</a>`,
        images: [imgTambosFullWeb, imgTambos],
        tech: [
          {
            name: "Vue.js",
            color: "grey--text text--darken-4",
            background: "#42B883",
            id: 1,
          },
          {
            name: "JavaScript",
            color: "grey--text text--darken-4",
            background: "#F0D900",
            id: 2,
          },
          {
            name: "HTML",
            color: "white",
            background: "#F16529",
            id: 3,
          },
          {
            name: "CSS",
            color: "white",
            background: "#13A1DF",
            id: 4,
          },
        ],
      },
      {
        id: 7,
        title: "BlockDay",
        subtitle: "iOS App",
        shortTitle: "BlockDay",
        year: 2026,
        img: workBlockDay,
        mobileOnly: true,
        highlight: "badge-wip",
        desc: `BlockDay is an iOS app built with SwiftUI around time blocking: instead of an endless to-do list, the day is planned as blocks of time assigned to the things that actually matter. Each block has its own color, a daily goal in minutes, and a progress bar that fills as time is tracked, so a glance is enough to know how the day is going. From the dashboard you can drag blocks into the day, start and stop them, and review your habits over time in the analysis section.
        <br> <br> The interface follows a dark, calm, and typographic design language, with generous spacing and subtle animations that keep the focus on the content. Technically, the project is my way into native development with SwiftUI and Swift: declarative views, state management, local data persistence, and Apple's Human Interface Guidelines. It's currently in development and not published on the App Store yet.`,
        desc_esp: `BlockDay es una app para iOS desarrollada con SwiftUI y basada en la administración del tiempo por bloques: en lugar de una lista infinita de pendientes, el día se planea como bloques de tiempo asignados a lo que realmente importa. Cada bloque tiene su propio color, una meta diaria en minutos y una barra de progreso que se llena conforme se registra el tiempo, de modo que un vistazo basta para saber cómo va el día. Desde el dashboard se pueden arrastrar bloques al día, iniciarlos y detenerlos, y revisar los hábitos a lo largo del tiempo en la sección de análisis.
        <br> <br> La interfaz sigue un lenguaje de diseño oscuro, sobrio y tipográfico, con espaciados amplios y animaciones sutiles que mantienen la atención en el contenido. En lo técnico, el proyecto es mi entrada al desarrollo nativo con SwiftUI y Swift: vistas declarativas, manejo de estado, persistencia local de datos y las Human Interface Guidelines de Apple. Actualmente se encuentra en desarrollo y aún no está publicada en la App Store.`,
        images: [imgBlockDayFullApp, imgBlockDay],
        tech: [
          {
            name: "SwiftUI",
            color: "white",
            background: "#0071E3",
            id: 1,
          },
          {
            name: "Swift",
            color: "white",
            background: "#F05138",
            id: 2,
          },
          {
            name: "iOS",
            color: "white",
            background: "#242424",
            id: 3,
          },
        ],
      },
    ],
    dialogProject: false,
    workSelected: null,
    tab: 0,
  }),

  created() {
    window.work = this;
  },

  methods: {
    openWork(item) {
      this.dialogProject = true;
      this.workSelected = item;
      this.tab = 0;
    },

    closeWork() {
      this.dialogProject = false;
      this.workSelected = null;
      this.tab = 0;
    },
  },
};
</script>

<style scoped>
.project-desc ::v-deep .project-cta {
  display: inline-block;
  margin-top: 8px;
  padding: 10px 26px;
  background-color: #2e7d32;
  color: #ffffff;
  font-weight: 600;
  text-decoration: none;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.35);
  transition: transform 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.project-desc ::v-deep .project-cta:hover {
  background-color: #1b5e20;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(46, 125, 50, 0.45);
}

.work-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;

  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;

  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;

  animation: badge-glow 2.4s ease-in-out infinite;
}

.work-badge--inline {
  position: static;
  font-size: 12px;
  padding: 6px 14px;
}

.work-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #ffffff;
  animation: badge-pulse 1.6s ease-in-out infinite;
}

@keyframes badge-glow {
  0%,
  100% {
    box-shadow: 0 2px 10px rgba(46, 125, 50, 0.45);
  }
  50% {
    box-shadow: 0 2px 18px rgba(102, 187, 106, 0.8);
  }
}

@keyframes badge-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.7);
  }
}

@media (prefers-reduced-motion: reduce) {
  .work-badge,
  .work-badge-dot {
    animation: none;
  }
}

.section-title {
  font-size: 128px;
  font-weight: bold;
  color: #212121;
  margin-bottom: 0px;
}

.section-title-responsive {
  font-size: clamp(2.5rem, 15vw, 5rem);
  font-weight: bold;
  color: #212121;
  margin-bottom: 0px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
}

.card-full-height {
  height: 197px;
}

@media (max-width: 600px) {
  .card-full-height {
    max-height: 350px;
  }
  .img-container {
    height: 100%;
  }
}

.img-container {
  position: relative;
  background-size: 127%;
  background-position-x: center;
  background-repeat: no-repeat;

  transition: all 0.5s ease;

  width: 100%;
}

.img-container:hover {
  background-size: 130%;
}

.card-year {
  font-size: 20px;
}

.card-title {
  font-size: 32px;
  line-height: normal;
}

.container-info {
  display: flex;
  flex-flow: column;
  justify-content: end;
  padding: 12px 8px;
  border-radius: 8px;

  height: 100%;

  opacity: 1;
  transition: all 0.3s ease-out;
  background-color: #24242450;
  cursor: pointer;
}

.opacity-info {
  opacity: 0;
}

.fixed-header-card {
  position: sticky;
  z-index: 200;
  top: 0;
}

.dialog-img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
}
</style>
